if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.handlePointerDown = this.handlePointerDown.bind(this);
        this.handlePointerMove = this.handlePointerMove.bind(this);
        this.handlePointerUp = this.handlePointerUp.bind(this);
        this.handlePointerCancel = this.handlePointerCancel.bind(this);
        this.handleTouchStart = this.handleTouchStart.bind(this);
        this.handleTouchMove = this.handleTouchMove.bind(this);
        this.handleTouchEnd = this.handleTouchEnd.bind(this);
        this.handleTouchCancel = this.handleTouchCancel.bind(this);
        this.handleResize = this.handleResize.bind(this);
        this.handleNavPrevious = this.handleNavPrevious.bind(this);
        this.handleNavNext = this.handleNavNext.bind(this);

        this.swipeThreshold = 30;
        this.dragThresholdRatio = 0.22;
        this.dragResistance = 0.35;
        this.verticalGestureBreakRatio = 1.35;
        this.resetInteractionState();
      }

      show(opener) {
        super.show(opener);
        this.cacheElements();
        this.setMediaItems();
        this.updateSlideMetrics();
        this.showActiveMedia(opener?.getAttribute('data-media-id'), false);
        this.setupNavigation();
        this.setupSwipeListeners();
        window.addEventListener('resize', this.handleResize);
      }

      hide() {
        this.teardownSwipeListeners();
        this.teardownNavigation();
        window.removeEventListener('resize', this.handleResize);
        super.hide();
        this.resetInteractionState();
      }

      cacheElements() {
        this.swipeContainer = this.querySelector('.product-media-modal__content');
        this.track = this.querySelector('.product-media-modal__track');
        this.prevNav = this.querySelector('.product-media-modal__nav--prev');
        this.nextNav = this.querySelector('.product-media-modal__nav--next');
      }

      setMediaItems() {
        if (!this.track) {
          this.mediaItems = [];
          return;
        }
        this.mediaItems = Array.from(this.track.querySelectorAll('.product-media-modal__slide'));
      }

      showActiveMedia(targetMediaId, animate = true) {
        if (!this.mediaItems || !this.mediaItems.length) return;

        let index = 0;
        let mediaId = targetMediaId;

        if (mediaId) {
          const locatedIndex = this.mediaItems.findIndex(
            (element) => element.getAttribute('data-media-id') === mediaId
          );
          if (locatedIndex !== -1) {
            index = locatedIndex;
          } else {
            mediaId = this.mediaItems[0].getAttribute('data-media-id');
          }
        } else {
          mediaId = this.mediaItems[0].getAttribute('data-media-id');
        }

        const isDifferentMedia = this.currentMediaId !== mediaId;
        this.currentIndex = index;
        this.currentMediaId = mediaId;

        this.mediaItems.forEach((slide, slideIndex) => {
          const isActive = slideIndex === index;
          slide.classList.toggle('active', isActive);
          slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
          if (isActive) slide.scrollTop = 0;
        });

        if (isDifferentMedia) {
          window.pauseAllMedia();
          this.ensureMediaLoaded(this.mediaItems[index]);
        }

        this.prefetchAdjacentMedia(index);

        this.updateSlideMetrics();
        this.setTrackPosition(this.getTranslateForIndex(index), animate && !this.isDragging);
        this.updateNavState();
      }

      ensureMediaLoaded(slide) {
        if (!slide) return;
        const mediaElement =
          slide.querySelector('[data-media-id]:not(.product-media-modal__slide)') || slide.firstElementChild;

        if (!mediaElement) return;

        if (mediaElement.nodeName === 'DEFERRED-MEDIA' || mediaElement.nodeName === 'PRODUCT-MODEL') {
          if (typeof mediaElement.loadContent === 'function' && mediaElement.getAttribute('loaded') !== 'true') {
            mediaElement.loadContent(false);
          }
          return;
        }

        const image = mediaElement.nodeName === 'IMG' ? mediaElement : mediaElement.querySelector('img');
        if (image) {
          image.loading = 'eager';
          if (image.decode) {
            image.decode().catch(() => {});
          }
        }
      }

      prefetchAdjacentMedia(index) {
        if (!this.mediaItems || !this.mediaItems.length) return;
        [-1, 1].forEach((offset) => {
          const slide = this.mediaItems[index + offset];
          if (slide) this.ensureMediaLoaded(slide);
        });
      }

      setupNavigation() {
        if (!this.prevNav || !this.nextNav || this.navListenersAttached) return;
        this.prevNav.addEventListener('click', this.handleNavPrevious);
        this.nextNav.addEventListener('click', this.handleNavNext);
        this.navListenersAttached = true;
        this.updateNavState();
      }

      teardownNavigation() {
        if (!this.navListenersAttached) return;
        if (this.prevNav) this.prevNav.removeEventListener('click', this.handleNavPrevious);
        if (this.nextNav) this.nextNav.removeEventListener('click', this.handleNavNext);
        this.navListenersAttached = false;
      }

      handleNavPrevious() {
        this.showAdjacentMedia(-1);
      }

      handleNavNext() {
        this.showAdjacentMedia(1);
      }

      updateNavState() {
        if (!this.prevNav || !this.nextNav) return;
        const hasMultipleSlides = this.mediaItems && this.mediaItems.length > 1;
        this.prevNav.classList.toggle('product-media-modal__nav--hidden', !hasMultipleSlides);
        this.nextNav.classList.toggle('product-media-modal__nav--hidden', !hasMultipleSlides);
        if (!hasMultipleSlides) return;

        const isFirst = this.currentIndex === 0;
        const isLast = this.currentIndex === this.mediaItems.length - 1;
        this.prevNav.toggleAttribute('disabled', isFirst);
        this.nextNav.toggleAttribute('disabled', isLast);
      }

      setupSwipeListeners() {
        if (this.swipeListenersAttached) return;
        this.gestureSurface = this.track || this.swipeContainer;
        if (!this.gestureSurface) return;

        this.usingPointerEvents = Boolean(window.PointerEvent);

        if (this.usingPointerEvents) {
          this.gestureSurface.addEventListener('pointerdown', this.handlePointerDown, { passive: true });
          this.gestureSurface.addEventListener('pointermove', this.handlePointerMove, { passive: false });
          this.gestureSurface.addEventListener('pointerup', this.handlePointerUp);
          this.gestureSurface.addEventListener('pointercancel', this.handlePointerCancel);
        } else {
          this.gestureSurface.addEventListener('touchstart', this.handleTouchStart, { passive: true });
          this.gestureSurface.addEventListener('touchmove', this.handleTouchMove, { passive: false });
          this.gestureSurface.addEventListener('touchend', this.handleTouchEnd);
          this.gestureSurface.addEventListener('touchcancel', this.handleTouchCancel);
        }

        this.swipeListenersAttached = true;
      }

      teardownSwipeListeners() {
        if (!this.swipeListenersAttached || !this.gestureSurface) return;

        if (this.usingPointerEvents) {
          this.gestureSurface.removeEventListener('pointerdown', this.handlePointerDown);
          this.gestureSurface.removeEventListener('pointermove', this.handlePointerMove);
          this.gestureSurface.removeEventListener('pointerup', this.handlePointerUp);
          this.gestureSurface.removeEventListener('pointercancel', this.handlePointerCancel);
        } else {
          this.gestureSurface.removeEventListener('touchstart', this.handleTouchStart);
          this.gestureSurface.removeEventListener('touchmove', this.handleTouchMove);
          this.gestureSurface.removeEventListener('touchend', this.handleTouchEnd);
          this.gestureSurface.removeEventListener('touchcancel', this.handleTouchCancel);
        }

        this.swipeListenersAttached = false;
        this.gestureSurface = null;
        this.usingPointerEvents = null;
      }

      handlePointerDown(event) {
        if (event.pointerType !== 'touch' && event.pointerType !== 'pen') return;
        if (this.touchStartX !== null) return;
        this.startSwipe(event.clientX, event.clientY, event.pointerType, event.pointerId);
      }

      handlePointerMove(event) {
        if (!this.isTrackingPointer(event.pointerId, event.pointerType)) return;
        this.handleDragMove(event.clientX, event.clientY, event);
      }

      handlePointerUp(event) {
        if (!this.isTrackingPointer(event.pointerId, event.pointerType)) return;
        this.handleDragEnd(event.clientX, event.clientY);
      }

      handlePointerCancel(event) {
        if (!this.isTrackingPointer(event.pointerId, event.pointerType)) return;
        this.resetInteractionState();
        this.animateToCurrent();
      }

      handleTouchStart(event) {
        if (event.touches.length !== 1) {
          this.resetInteractionState();
          return;
        }
        if (this.touchStartX !== null) return;
        const touch = event.touches[0];
        this.startSwipe(touch.clientX, touch.clientY, 'touch', null);
      }

      handleTouchMove(event) {
        if (event.touches.length !== 1) return;
        if (!this.isTrackingPointer(null, 'touch')) return;
        const touch = event.touches[0];
        this.handleDragMove(touch.clientX, touch.clientY, event);
      }

      handleTouchEnd(event) {
        if (!event.changedTouches.length) return;
        if (!this.isTrackingPointer(null, 'touch')) {
          this.resetInteractionState();
          return;
        }
        const touch = event.changedTouches[0];
        this.handleDragEnd(touch.clientX, touch.clientY);
      }

      handleTouchCancel() {
        this.resetInteractionState();
        this.animateToCurrent();
      }

      startSwipe(x, y, pointerType, pointerId) {
        if (!this.mediaItems || this.mediaItems.length < 2) return;
        this.updateSlideMetrics();
        this.touchStartX = x;
        this.touchStartY = y;
        this.activePointerType = pointerType;
        this.activePointerId = pointerId;
        this.dragRawDeltaX = 0;
        this.dragAdjustedDeltaX = 0;
        this.dragDirectionDecided = false;
        this.isDragging = false;
      }

      handleDragMove(x, y, originalEvent) {
        if (this.touchStartX === null || this.touchStartY === null) return;

        const deltaX = x - this.touchStartX;
        const deltaY = y - this.touchStartY;

        if (!this.dragDirectionDecided) {
          const absDeltaX = Math.abs(deltaX);
          const absDeltaY = Math.abs(deltaY);
          if (absDeltaX < 8 && absDeltaY < 8) return;

          if (absDeltaY > absDeltaX * this.verticalGestureBreakRatio) {
            this.resetInteractionState();
            return;
          }

          if (absDeltaX < 8) return;

          this.dragDirectionDecided = true;
          this.isDragging = true;
          if (this.track) {
            this.track.style.transition = 'none';
          }
        }

        if (!this.isDragging) return;

        if (originalEvent && typeof originalEvent.preventDefault === 'function' && originalEvent.cancelable) {
          originalEvent.preventDefault();
        }

        let adjustedDeltaX = deltaX;
        if (
          (this.currentIndex === 0 && deltaX > 0) ||
          (this.currentIndex === this.mediaItems.length - 1 && deltaX < 0)
        ) {
          adjustedDeltaX = deltaX * this.dragResistance;
        }

        this.dragRawDeltaX = deltaX;
        this.dragAdjustedDeltaX = adjustedDeltaX;
        this.applyDragTranslation(adjustedDeltaX);
      }

      handleDragEnd(x, y) {
        if (this.touchStartX === null || this.touchStartY === null) {
          this.resetInteractionState();
          return;
        }

        const wasDragging = this.isDragging;
        const rawDeltaX = this.dragRawDeltaX || x - this.touchStartX;
        const deltaY = y - this.touchStartY;

        this.resetInteractionState();

        if (!wasDragging) {
          if (Math.abs(rawDeltaX) < this.swipeThreshold) return;
          if (Math.abs(deltaY) > Math.abs(rawDeltaX)) return;
          this.showAdjacentMedia(rawDeltaX < 0 ? 1 : -1);
          return;
        }

        const threshold = Math.min(this.slideWidth * this.dragThresholdRatio, 180);
        if (Math.abs(rawDeltaX) > threshold) {
          const direction = rawDeltaX < 0 ? 1 : -1;
          if (this.canMove(direction)) {
            this.showAdjacentMedia(direction);
            return;
          }
        }

        this.animateToCurrent();
      }

      resetInteractionState() {
        this.touchStartX = null;
        this.touchStartY = null;
        this.activePointerType = null;
        this.activePointerId = null;
        this.dragRawDeltaX = 0;
        this.dragAdjustedDeltaX = 0;
        this.dragDirectionDecided = false;
        this.isDragging = false;
      }

      applyDragTranslation(deltaX) {
        if (!this.track) return;
        const base = this.getTranslateForIndex(this.currentIndex);
        this.track.style.transform = `translateX(${base + deltaX}px)`;
      }

      canMove(direction) {
        const targetIndex = this.currentIndex + direction;
        return targetIndex >= 0 && targetIndex < this.mediaItems.length;
      }

      showAdjacentMedia(step, animate = true) {
        if (!this.mediaItems || !this.mediaItems.length) return;
        const targetIndex = this.currentIndex + step;
        if (targetIndex < 0 || targetIndex >= this.mediaItems.length) {
          this.animateToCurrent();
          return;
        }
        const targetSlide = this.mediaItems[targetIndex];
        const targetMediaId = targetSlide.getAttribute('data-media-id');
        this.showActiveMedia(targetMediaId, animate);
      }

      animateToCurrent() {
        this.setTrackPosition(this.getTranslateForIndex(this.currentIndex), true);
        this.updateNavState();
      }

      getTranslateForIndex(index) {
        if (!this.mediaItems || !this.mediaItems.length) return 0;
        const slide = this.mediaItems[index] || this.mediaItems[0];
        if (!slide) return 0;
        return -slide.offsetLeft;
      }

      setTrackPosition(position, animate) {
        if (!this.track) return;
        if (!animate) {
          this.track.style.transition = 'none';
          this.track.style.transform = `translateX(${position}px)`;
          this.track.getBoundingClientRect();
          requestAnimationFrame(() => {
            if (this.track) this.track.style.transition = '';
          });
        } else {
          this.track.style.transition = '';
          this.track.style.transform = `translateX(${position}px)`;
        }
      }

      updateSlideMetrics() {
        if (!this.swipeContainer) return;
        const styles = window.getComputedStyle(this.swipeContainer);
        const paddingLeft = parseFloat(styles.paddingLeft) || 0;
        const paddingRight = parseFloat(styles.paddingRight) || 0;
        if (this.mediaItems && this.mediaItems.length) {
          const slide = this.mediaItems[this.currentIndex] || this.mediaItems[0];
          this.slideWidth = slide ? slide.getBoundingClientRect().width : 0;
        } else {
          this.slideWidth = this.swipeContainer.clientWidth - paddingLeft - paddingRight;
        }
      }

      handleResize() {
        this.updateSlideMetrics();
        this.setTrackPosition(this.getTranslateForIndex(this.currentIndex || 0), false);
      }

      isTrackingPointer(pointerId, pointerType) {
        if (this.touchStartX === null || this.touchStartY === null) return false;
        if (this.activePointerId !== null && pointerId !== null && pointerId !== this.activePointerId) return false;
        if (this.activePointerType && pointerType && pointerType !== this.activePointerType) return false;
        return true;
      }
    }
  );
}
